import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Heart, Calendar, AlertTriangle, 
  FileText, Activity, Search, Pill, MessageCircle, X, User,
  Shield, Camera, Thermometer, Clipboard, XCircle, CheckCircle,
  Eye, MapPin, Zap, Users, TrendingUp, DollarSign, Handshake,
  BookOpen, Download, Star, Moon, Brain, HelpCircle, Stethoscope,
  Target, Lightbulb, Clock, Share2, Check, Copy, Play
} from 'lucide-react';
import TTSPlayer from './TTSPlayer';
import '../styles/ArticleSlideshow.css';

const iconMap = {
  Heart: Heart,
  Calendar: Calendar,
  AlertTriangle: AlertTriangle,
  FileText: FileText,
  Activity: Activity,
  Search: Search,
  Pill: Pill,
  MessageCircle: MessageCircle,
  Shield: Shield,
  Camera: Camera,
  Thermometer: Thermometer,
  Clipboard: Clipboard,
  XCircle: XCircle,
  CheckCircle: CheckCircle,
  Eye: Eye,
  MapPin: MapPin,
  Zap: Zap,
  Users: Users,
  TrendingUp: TrendingUp,
  DollarSign: DollarSign,
  Handshake: Handshake,
  BookOpen: BookOpen,
  Download: Download,
  Star: Star,
  Moon: Moon,
  Brain: Brain,
  HelpCircle: HelpCircle,
  Stethoscope: Stethoscope,
  Target: Target,
  Lightbulb: Lightbulb,
  Clock: Clock
};

// Mini Pie Chart Component for percentage visualization
const MiniPieChart = ({ percent, label, color = '#4A0E4E' }) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;
  
  return (
    <div className="mini-pie-chart">
      <svg width="90" height="90" viewBox="0 0 90 90">
        <circle
          cx="45"
          cy="45"
          r={radius}
          fill="none"
          stroke="rgba(0,0,0,0.1)"
          strokeWidth="10"
        />
        <circle
          cx="45"
          cy="45"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform="rotate(-90 45 45)"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
        <text x="45" y="45" textAnchor="middle" dy="0.3em" className="pie-percent">
          {percent}%
        </text>
      </svg>
      {label && <span className="pie-label">{label}</span>}
    </div>
  );
};

// Horizontal Bar Chart Component for multiple items
const HorizontalBarChart = ({ data, maxValue = 100 }) => {
  const colors = ['#4A0E4E', '#D4AF37', '#7C3AED', '#059669', '#E11D48'];
  
  return (
    <div className="horizontal-bar-chart">
      {data.map((item, idx) => (
        <div key={idx} className="bar-item">
          <div className="bar-header">
            <span className="bar-name">{item.label}</span>
            <span className="bar-percent">{item.value}%</span>
          </div>
          <div className="bar-track">
            <div 
              className="bar-progress" 
              style={{ 
                width: `${(item.value / maxValue) * 100}%`,
                backgroundColor: colors[idx % colors.length]
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Donut Chart with center label
const DonutChart = ({ segments, centerLabel, centerValue }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const colors = ['#4A0E4E', '#D4AF37', '#7C3AED', '#059669', '#E11D48', '#F59E0B'];
  
  // Pre-calculate offsets for each segment
  const segmentsWithOffsets = segments.reduce((acc, segment, idx) => {
    const strokeDasharray = (segment.value / 100) * circumference;
    const offset = idx === 0 ? 0 : acc[idx - 1].cumulativeOffset;
    acc.push({
      ...segment,
      strokeDasharray,
      offset,
      cumulativeOffset: offset + strokeDasharray
    });
    return acc;
  }, []);
  
  return (
    <div className="donut-chart">
      <svg width="120" height="120" viewBox="0 0 120 120">
        {segmentsWithOffsets.map((segment, idx) => (
          <circle
            key={idx}
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={colors[idx % colors.length]}
            strokeWidth="16"
            strokeDasharray={`${segment.strokeDasharray} ${circumference}`}
            strokeDashoffset={-segment.offset}
            transform="rotate(-90 60 60)"
            style={{ transition: 'all 0.5s ease' }}
          />
        ))}
        <text x="60" y="55" textAnchor="middle" className="donut-value">
          {centerValue}
        </text>
        <text x="60" y="72" textAnchor="middle" className="donut-label">
          {centerLabel}
        </text>
      </svg>
      <div className="donut-legend">
        {segments.map((segment, idx) => (
          <div key={idx} className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: colors[idx % colors.length] }} />
            <span className="legend-text">{segment.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ArticleSlideshow = ({ article, onClose, startAtIntro = false }) => {
  // Start at -1 (intro slide) if coming from shared link, otherwise start at 0
  const [currentSlide, setCurrentSlide] = useState(startAtIntro ? -1 : 0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [shareStatus, setShareStatus] = useState(null); // 'copied' | 'shared' | null
  const slides = article.slides || [];
  const totalSlides = slides.length;
  const showingIntro = currentSlide === -1;

  // Minimum swipe distance for navigation (in px)
  const minSwipeDistance = 50;

  // Hide chat widget when slideshow is open
  useEffect(() => {
    document.body.classList.add('slideshow-open');
    return () => {
      document.body.classList.remove('slideshow-open');
    };
  }, []);

  // Share functionality
  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/articles?guide=${article.id}&utm_source=guide_share&utm_content=${article.id}`;
    const shareText = `CADENCE believes you deserve to know: ${article.title}`;
    
    // Track the share attempt
    const trackShare = async (method) => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        await fetch(`${API_URL}/api/track-share`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            guide_id: article.id,
            guide_title: article.title,
            share_method: method
          })
        });
      } catch (err) {
        // Don't block sharing if tracking fails
        console.log('Share tracking skipped');
      }
    };
    
    // Try native share first (works on mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: shareText,
          url: shareUrl
        });
        setShareStatus('shared');
        await trackShare('native');
        setTimeout(() => setShareStatus(null), 2000);
      } catch (err) {
        // User cancelled or share failed - fall back to copy
        if (err.name !== 'AbortError') {
          copyToClipboard(shareUrl, shareText);
          await trackShare('copy');
        }
      }
    } else {
      // Fallback: copy to clipboard
      copyToClipboard(shareUrl, shareText);
      await trackShare('copy');
    }
  };

  const copyToClipboard = async (url, text) => {
    try {
      await navigator.clipboard.writeText(`${text}\n\n${url}`);
      setShareStatus('copied');
      setTimeout(() => setShareStatus(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
      // Reset scroll to top of next slide
      const slideContent = document.querySelector('.slide-content');
      if (slideContent) {
        slideContent.scrollTo(0, 0);
      }
    }
  };

  const prevSlide = () => {
    if (currentSlide > -1) {  // Allow going back to intro slide
      setCurrentSlide(currentSlide - 1);
      // Reset scroll to top of previous slide
      const slideContent = document.querySelector('.slide-content');
      if (slideContent) {
        slideContent.scrollTo(0, 0);
      }
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    // Reset scroll to top
    const slideContent = document.querySelector('.slide-content');
    if (slideContent) {
      slideContent.scrollTo(0, 0);
    }
  };

  const startGuide = () => {
    setCurrentSlide(0);
    const slideContent = document.querySelector('.slide-content');
    if (slideContent) {
      slideContent.scrollTo(0, 0);
    }
  };

  // Touch handlers for swipe navigation
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Handle click on slide content to advance (optional tap navigation)
  const handleSlideClick = (e) => {
    // Don't navigate if clicking on buttons or links
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'A' || e.target.closest('button')) {
      return;
    }
    
    // Get click position relative to slide content
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    
    // Click on right third = next, left third = prev
    if (clickX > width * 0.66) {
      nextSlide();
    } else if (clickX < width * 0.33) {
      prevSlide();
    }
  };

  if (!slides.length) return null;

  const slide = showingIntro ? null : slides[currentSlide];
  const IconComponent = slide ? (iconMap[slide.icon] || Heart) : BookOpen;

  return (
    <div className="slideshow-overlay" data-testid="article-slideshow">
      <div className="slideshow-container">
        {/* Header */}
        <div className="slideshow-header">
          <div className="slideshow-meta">
            <span className="slide-counter">
              {showingIntro ? 'Intro' : `${currentSlide + 1} / ${totalSlides}`}
            </span>
            <h2 className="slideshow-title">{article.title}</h2>
          </div>
          <button className="slideshow-close" onClick={onClose} data-testid="close-slideshow">
            <X size={24} />
          </button>
        </div>

        {/* Progress Bar - only show when not on intro */}
        {!showingIntro && (
          <div className="slideshow-progress">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`progress-dot ${index === currentSlide ? 'active' : ''} ${index < currentSlide ? 'completed' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* TTS Player - only show when not on intro */}
        {!showingIntro && (
          <div className="slideshow-tts-wrapper">
            <TTSPlayer
              slides={slides}
              currentSlide={currentSlide}
              onSlideChange={goToSlide}
              guideTitle={article.title}
            />
          </div>
        )}

        {/* INTRO SLIDE - Title Card */}
        {showingIntro && (
          <div className="slide-content intro-slide">
            <div className="intro-card">
              {article.image && (
                <div className="intro-image">
                  <img src={article.image} alt={article.title} />
                  <div className="intro-image-overlay" />
                </div>
              )}
              <div className="intro-content">
                <span className="intro-category">{article.category}</span>
                <h1 className="intro-title">{article.title}</h1>
                {article.description && (
                  <p className="intro-description">{article.description}</p>
                )}
                <div className="intro-meta">
                  <span className="intro-slides">
                    <FileText size={16} />
                    {totalSlides} slides
                  </span>
                  {article.readTime && (
                    <span className="intro-time">
                      <Clock size={16} />
                      {article.readTime}
                    </span>
                  )}
                </div>
                <button 
                  className="intro-start-btn"
                  onClick={startGuide}
                  data-testid="start-guide-btn"
                >
                  <Play size={20} />
                  Start Guide
                </button>
              </div>
            </div>
          </div>
        )}

        {/* REGULAR SLIDE CONTENT */}
        {!showingIntro && (
          <div 
            className="slide-content"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onClick={handleSlideClick}
            style={{ cursor: 'pointer' }}
          >
            {/* Slide Image (if available) */}
            {slide.image && (
              <div className="slide-image">
                <img src={slide.image} alt={slide.title} />
              </div>
            )}
            
            <div className="slide-icon">
              <IconComponent size={48} />
            </div>
          
            <h3 className="slide-title">{slide.title}</h3>
          
          {slide.content && (
            <p className="slide-text">{slide.content}</p>
          )}

          {slide.highlight && (
            <div className="slide-highlight">
              <AlertTriangle size={20} />
              <span>{slide.highlight}</span>
            </div>
          )}

          {slide.bullets && (
            <ul className="slide-bullets">
              {slide.bullets.map((bullet, idx) => (
                <li key={idx}>{bullet}</li>
              ))}
            </ul>
          )}

          {slide.columns && (
            <div className="slide-columns">
              {slide.columns.map((col, idx) => (
                <div key={idx} className="slide-column">
                  <h4>{col.header}</h4>
                  <ul>
                    {col.items.map((item, itemIdx) => (
                      <li key={itemIdx}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {slide.cards && (
            <div className="slide-cards">
              {slide.cards.map((card, idx) => (
                <div key={idx} className="stat-card">
                  {/* Visual bar for comparison stats */}
                  {card.comparison && (
                    <div className="stat-comparison">
                      <div className="comparison-bars">
                        <div className="comparison-bar baseline">
                          <span className="bar-label">{card.comparison.baseline.label}</span>
                          <div className="bar-fill" style={{ width: '40%' }}></div>
                          <span className="bar-value">{card.comparison.baseline.value}</span>
                        </div>
                        <div className="comparison-bar highlight">
                          <span className="bar-label">{card.comparison.highlight.label}</span>
                          <div className="bar-fill" style={{ width: `${card.comparison.highlight.percent}%` }}></div>
                          <span className="bar-value">{card.comparison.highlight.value}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Mini pie chart for percentage cards */}
                  {card.showPie && card.stat && (
                    <MiniPieChart 
                      percent={parseInt(card.stat)} 
                      label={card.title}
                      color={card.pieColor || '#4A0E4E'}
                    />
                  )}
                  {!card.showPie && (
                    <>
                      <div className="stat-number">{card.title}</div>
                      <div className="stat-title">{card.stat}</div>
                    </>
                  )}
                  <div className="stat-desc">{card.desc}</div>
                </div>
              ))}
            </div>
          )}

          {/* Horizontal Bar Chart */}
          {slide.barChart && (
            <div className="chart-container">
              <HorizontalBarChart data={slide.barChart} maxValue={slide.barChartMax || 100} />
            </div>
          )}

          {/* Donut Chart */}
          {slide.donutChart && (
            <div className="chart-container">
              <DonutChart 
                segments={slide.donutChart.segments} 
                centerLabel={slide.donutChart.centerLabel}
                centerValue={slide.donutChart.centerValue}
              />
            </div>
          )}

          {/* Pie Charts Grid */}
          {slide.pieCharts && (
            <div className="pie-charts-grid">
              {slide.pieCharts.map((pie, idx) => (
                <MiniPieChart 
                  key={idx}
                  percent={pie.percent} 
                  label={pie.label}
                  color={pie.color}
                />
              ))}
            </div>
          )}

          {/* Only show slide CTA if not on last slide (last slide has end-guide CTA) */}
          {slide.cta && currentSlide !== totalSlides - 1 && (
            <div className="slide-cta">
              <p>Need help advocating for yourself?</p>
              <Link to="/contact" className="btn-primary" onClick={onClose}>
                Request a Consultation
              </Link>
            </div>
          )}

          {/* End of Guide CTA - Shows on last slide */}
          {currentSlide === totalSlides - 1 && (
            <div className="end-guide-cta">
              <div className="end-guide-actions">
                <button 
                  className={`end-action-btn share-btn primary ${shareStatus ? 'success' : ''}`}
                  onClick={handleShare}
                  data-testid="end-share-guide"
                >
                  {shareStatus ? <Check size={20} /> : <Share2 size={20} />}
                  <span>{shareStatus === 'copied' ? 'Copied!' : shareStatus === 'shared' ? 'Shared!' : 'Share This Guide'}</span>
                </button>
                <Link to="/articles" className="end-action-btn" onClick={onClose}>
                  <BookOpen size={20} />
                  <span>Explore More Guides</span>
                </Link>
                <button className="end-action-btn back-btn gold" onClick={onClose}>
                  <ChevronLeft size={20} />
                  <span>Return to CADENCE Compass</span>
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Navigation - show different options for intro vs regular slides */}
        {!showingIntro && (
          <div className="slideshow-nav">
            <button 
              className="nav-btn prev" 
              onClick={prevSlide} 
              disabled={currentSlide === 0}
              data-testid="prev-slide"
            >
              <ChevronLeft size={24} />
              <span>Previous</span>
            </button>
            
            {/* Share button - available on every slide */}
            <button 
              className={`nav-btn share-nav ${shareStatus ? 'success' : ''}`}
              onClick={handleShare}
              data-testid="nav-share-guide"
              title="Share this guide"
            >
              {shareStatus ? <Check size={20} /> : <Share2 size={20} />}
            </button>
            
            <button 
              className="nav-btn next" 
              onClick={nextSlide}
              disabled={currentSlide === totalSlides - 1}
              data-testid="next-slide"
            >
              <span>{currentSlide === totalSlides - 1 ? 'Done' : 'Next'}</span>
              <ChevronRight size={24} />
            </button>
          </div>
        )}

        {/* Author */}
        <div className="slideshow-author">
          <User size={16} />
          <span>{article.author}</span>
          <span className="author-divider">•</span>
          <span>{article.authorRole}</span>
        </div>
      </div>
    </div>
  );
};

export default ArticleSlideshow;
